import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  Typography,
  FormControlLabel,
  Switch,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Tabs,
  Tab,
  Box,
  Alert,
  Stack,
} from '@mui/material';
import { ICompany } from '../CompaniesManagementPage';
import { updateCompany, createCompany } from '../services';

interface EditCompanyFormProps {
  company: ICompany | null;
  isNew: boolean;
  open: boolean;
  onClose: () => void;
  onSave: (updatedCompany: ICompany) => Promise<void>;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`company-edit-tabpanel-${index}`}
      aria-labelledby={`company-edit-tab-${index}`}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `company-edit-tab-${index}`,
    'aria-controls': `company-edit-tabpanel-${index}`,
  };
}

const EditCompanyForm: React.FC<EditCompanyFormProps> = ({ company, isNew, open, onClose, onSave }) => {
  const [editedCompany, setEditedCompany] = useState<ICompany | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [confirmationOpen, setConfirmationOpen] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form with company data when it changes
  useEffect(() => {
    if (company) {
      setEditedCompany({ ...company });
    } else if (isNew) {
      // Create empty company data for a new company
      setEditedCompany({
        id: 0, // This will be ignored when creating a new company
        name: '',
        contact: {
          name: null,
          phone: null,
          email: null,
        },
        address: {
          street: null,
          city: null,
          state: null,
          zip: null,
          country: null,
        },
        status: true, // Default to active status
      });
    }
  }, [company, isNew]);

  // Handle text field changes
  const handleTextChange =
    (field: string, subField?: string, subSubField?: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!editedCompany) return;

      const { value } = event.target;

      // Handle nested fields (contact, address)
      if (subField && subSubField) {
        // This shouldn't happen in our current structure
      } else if (subField) {
        setEditedCompany({
          ...editedCompany,
          [subField]: {
            ...(editedCompany[subField as keyof ICompany] as Record<string, any>),
            [field]: value || null,
          },
        });
      } else {
        // Handle simple fields
        setEditedCompany({
          ...editedCompany,
          [field]: value,
        });
      }
    };

  // Handle status toggle
  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!editedCompany) return;

    setEditedCompany({
      ...editedCompany,
      status: event.target.checked,
    });
  };

  // Open confirmation dialog before saving
  const handleApply = () => {
    setConfirmationOpen(true);
  };

  // Close confirmation dialog without saving
  const handleCancelConfirm = () => {
    setConfirmationOpen(false);
  };

  // Alias for handleCancelConfirm to match naming in Dialog component
  const handleCloseConfirmation = handleCancelConfirm;

  // Close main dialog
  const handleCloseDialog = () => {
    setError(null);
    onClose();
  };

  // Save changes after confirmation
  // Get button text based on current state
  const getButtonText = () => {
    return isNew ? 'Create' : 'Apply Changes';
  };

  const handleConfirm = async () => {
    if (!editedCompany) return;

    setSaving(true);
    setError(null);

    try {
      // Prepare data for API
      const companyData = {
        name: editedCompany.name,
        contact: {
          name: editedCompany.contact.name,
          phone: editedCompany.contact.phone,
          email: editedCompany.contact.email,
        },
        address: {
          street: editedCompany.address.street,
          city: editedCompany.address.city,
          state: editedCompany.address.state,
          zip: editedCompany.address.zip,
          country: editedCompany.address.country,
        },
        status: editedCompany.status,
      };

      let updatedCompanyData: ICompany | null;

      if (isNew) {
        // Create new company
        updatedCompanyData = await createCompany(companyData);
      } else {
        // Update existing company
        updatedCompanyData = await updateCompany(editedCompany.id, companyData);
      }

      if (updatedCompanyData) {
        // Pass the updated company to the parent component
        await onSave(updatedCompanyData);

        // Close dialogs
        setConfirmationOpen(false);
        handleCloseDialog();
      } else {
        throw new Error('Failed to save company data.');
      }
    } catch (err) {
      console.error('Error saving company:', err);
      setError(`Failed to save company: ${(err as Error).message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  if (!editedCompany) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
            <CircularProgress />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const dialogTitle = isNew ? 'Add New Company' : `Edit Company: ${editedCompany.name}`;
  const confirmTitle = isNew ? 'Confirm New Company' : 'Confirm Changes';
  const confirmMessage = isNew
    ? 'Are you sure you want to create this new company?'
    : `Are you sure you want to update the company information for ${editedCompany.name}?`;

  return (
    <>
      {/* Main Edit Dialog */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <Tabs value={0} aria-label="company edit tabs">
            <Tab label="Company Details" id={a11yProps(0).id} aria-controls={a11yProps(0)['aria-controls']} />
          </Tabs>

          <TabPanel value={0} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={editedCompany.name || ''}
                  onChange={handleTextChange('name')}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={editedCompany.status} onChange={handleStatusChange} color="primary" />}
                  label="Active"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">Contact Information</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Contact Name"
                  value={editedCompany.contact.name || ''}
                  onChange={handleTextChange('name', 'contact')}
                  fullWidth
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Contact Phone"
                  value={editedCompany.contact.phone || ''}
                  onChange={handleTextChange('phone', 'contact')}
                  fullWidth
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Contact Email"
                  value={editedCompany.contact.email || ''}
                  onChange={handleTextChange('email', 'contact')}
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  type="email"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">Address Information</Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={editedCompany.address.street || ''}
                  onChange={handleTextChange('street', 'address')}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="City"
                  value={editedCompany.address.city || ''}
                  onChange={handleTextChange('city', 'address')}
                  fullWidth
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  label="State"
                  value={editedCompany.address.state || ''}
                  onChange={handleTextChange('state', 'address')}
                  fullWidth
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  label="ZIP Code"
                  value={editedCompany.address.zip || ''}
                  onChange={handleTextChange('zip', 'address')}
                  fullWidth
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label="Country"
                  value={editedCompany.address.country || ''}
                  onChange={handleTextChange('country', 'address')}
                  fullWidth
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
            </Grid>
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleApply} color="primary" variant="contained" disabled={saving || !editedCompany.name}>
            {saving ? <CircularProgress size={24} /> : getButtonText()}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmationOpen} onClose={handleCancelConfirm}>
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{confirmMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmation}>Cancel</Button>
          <Button onClick={handleConfirm} color="primary" variant="contained" autoFocus disabled={saving}>
            {saving ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditCompanyForm;
